import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddCommentIcon from "@mui/icons-material/AddComment";
import { API_BASE_URL } from "../../../utils/api";

const CommentsTab = ({
  comments = [],
  onAddCommentClick,
  onImageClick,
  onEditCommentClick,
  onViewHistoryClick,
  userPermissions = [],
  notifyMember,
}) => {
  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ChatBubbleOutlineIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Comments
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review internal and external comments, attachments, and edit history.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {(notifyMember === 0 || notifyMember === false) && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <strong>Member opted out of notifications.</strong> Adding a comment will NOT send an email to this member. Mark the comment as Internal if it is for staff only.
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2.5,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddCommentIcon />}
              onClick={onAddCommentClick}
              type="button"
              sx={primaryButtonSx}
            >
              Add Comment
            </Button>
          </Box>

          {comments.length > 0 ? (
            <Stack spacing={2}>
              {comments.map((comment, index) => {
                const isInternal = !!comment.is_internal;

                return (
                  <Card
                    key={comment.comment_id ?? `${comment.user_name}-${comment.sent_at}-${index}`}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                      backgroundColor: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid rgba(47,99,173,0.08)",
                        backgroundColor: isInternal
                          ? "rgba(220,38,38,0.05)"
                          : "rgba(47,99,173,0.05)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label={isInternal ? "Internal Comment" : "External Comment"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          borderRadius: "999px",
                          color: isInternal ? "#b91c1c" : "#1d4ed8",
                          backgroundColor: isInternal
                            ? "rgba(220,38,38,0.12)"
                            : "rgba(59,130,246,0.12)",
                        }}
                      />

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {userPermissions.includes("edit_comments") && (
                          <Tooltip title="Edit Comment">
                            <IconButton
                              size="small"
                              onClick={() => onEditCommentClick?.(comment)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {comment.edited_at &&
                          userPermissions.includes("view_comment_history") && (
                            <Tooltip title="View Edit History">
                              <IconButton
                                size="small"
                                onClick={() => onViewHistoryClick?.(comment)}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                    </Box>

                    <Box sx={{ p: 2 }}>
                      {comment.comment_text && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#223142",
                            whiteSpace: "pre-wrap",
                            mb: 1.25,
                          }}
                        >
                          {comment.comment_text}
                        </Typography>
                      )}

                      {Array.isArray(comment.attachments) &&
                        comment.attachments.length > 0 && (
                          <Box sx={{ mt: 1.25 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
                            >
                              Attachments
                            </Typography>

                            <Stack spacing={1}>
                              {comment.attachments.map((file, i) => {
                                const fileUrl = `${API_BASE_URL}/workordercommentimages/${encodeURIComponent((file.file_url || "").split("/").pop() || file.file_name || "")}`;

                                const ext = (file.file_name || file.file_url || "")
                                  .split(".")
                                  .pop()
                                  ?.toLowerCase();

                                const isImage = [
                                  "jpg",
                                  "jpeg",
                                  "png",
                                  "gif",
                                  "bmp",
                                  "webp",
                                ].includes(ext);

                                return (
                                  <Box key={file.id ?? i}>
                                    {isImage ? (
                                      <Card
                                        elevation={0}
                                        sx={{
                                          p: 1,
                                          cursor: "pointer",
                                          maxWidth: 220,
                                          borderRadius: "12px",
                                          border: "1px solid rgba(47,99,173,0.08)",
                                          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                                        }}
                                        onClick={() => onImageClick?.(fileUrl)}
                                      >
                                        <img
                                          src={fileUrl}
                                          alt={file.file_name || "Attachment"}
                                          style={{
                                            width: "100%",
                                            borderRadius: "8px",
                                            display: "block",
                                          }}
                                        />
                                      </Card>
                                    ) : (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          backgroundColor: "white",
                                          borderRadius: "8px",
                                          px: 1,
                                          py: 0.75,
                                          border: "1px solid rgba(47,99,173,0.08)",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#1976d2",
                                            fontWeight: 500,
                                            wordBreak: "break-all",
                                          }}
                                        >
                                          <a
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: "#1976d2",
                                              textDecoration: "none",
                                            }}
                                          >
                                            {file.file_name ||
                                              file.file_url?.split("/").pop() ||
                                              "Download File"}
                                          </a>
                                        </Typography>

                                        <Tooltip title="Download">
                                          <IconButton
                                            size="small"
                                            component="a"
                                            href={fileUrl}
                                            download
                                          >
                                            <DownloadIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}

                      <Divider
                        sx={{ my: 1.5, borderColor: "rgba(47,99,173,0.08)" }}
                      />

                      <Typography variant="caption" sx={{ color: "#6b778c" }}>
                        Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                        {comment.sent_at
                          ? new Date(comment.sent_at).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                No comments available
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                Add the first comment for this work order.
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddCommentIcon />}
                  onClick={onAddCommentClick}
                  type="button"
                  sx={primaryButtonSx}
                >
                  Add Comment
                </Button>
              </Box>
            </Card>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default CommentsTab;